import { TimesheetData, TimesheetUpdateData } from "./timesheet-models";
import { TimesheetLeftTable } from "./timesheet-left-table";
import { TimesheetRightTable } from "./timesheet-right-table";
import { TimesheetRightTableDataManager, TimesheetTableData } from "./timesheet-right-table-data-manager";
import { TimesheetShortcutsSet } from "./timesheet-shortcuts-set";
import { EditableTableCalculationsData, EditableTableCalculator } from "../common/editable-table";
import { TimesheetCommentsManager } from "./timesheet-comments-manager";
import { Requester } from "../common/requester";
import { C } from "../common/component";
import { Helper } from "../common/helper";
import { TimesheetRightTableFormatter } from "./timesheet-right-table-formatter";

export class Timesheet
{
    private readonly calculator = new EditableTableCalculator(true);
    private leftTable: TimesheetLeftTable;
    private rightTable: TimesheetRightTable;
    private rightTableDataManager: TimesheetRightTableDataManager;

    private rightTableRowNumbersByRowId: { [id: number]: number[] } = {};
    private data: TimesheetData;

    private readonly parent;
    private readonly shortcuts: TimesheetShortcutsSet;
    private readonly width: number;

    constructor(parent, shortcuts: TimesheetShortcutsSet, width: number)
    {
        this.parent = parent;
        this.shortcuts = shortcuts;
        this.width = width;
    }

    setData(data: TimesheetData)
    {
        this.data = data;
        this.leftTable = new TimesheetLeftTable(data.LeftTable, this.width, data.IsCompact, data.RowHeight, this.calculator);
        setTimeout(async () =>
        {
            const rowsByEmployee = new Map();
            for (const rowId in data.RightTableRowsData)
            {
                const row = data.RightTableRowsData[rowId];
                if (!rowsByEmployee.has(row.EmployeeId))
                {
                    rowsByEmployee.set(row.EmployeeId, [rowId]);
                }
                else
                {
                    rowsByEmployee.get(row.EmployeeId).push(rowId);
                }
            }
            const groupedRows = [];
            rowsByEmployee.forEach((rowIds, _) => groupedRows.push(rowIds));
            for (const rows of groupedRows)
            {
                const calculationsData: EditableTableCalculationsData[] = await Requester.post(data.CalculationsDataUrl, rows);
                this.setCalculationsData(calculationsData);
                this.rightTable.setHeaderHeight(this.leftTable.getHeaderHeight());
            }
            // This is needed to update calculated header row
            this.rightTable.render();
        }, 100);

        const container = Helper.isMobileScreen()
            ? C.div
                .addClass("timesheet-container")
                .css({
                    display: "flex",
                    flexWrap: "nowrap",
                    overflowX: "auto",
                    width: "100%"
                })
                .appendTo(this.parent)
            : this.parent;

        this.leftTable.appendTo(container);
        this.leftTable.setFixedHeader();

        let rowNumber = 0;
        for (const rowId of data.RightTableRows)
        {
            if (!this.rightTableRowNumbersByRowId[rowId])
            {
                this.rightTableRowNumbersByRowId[rowId] = [];
            }
            this.rightTableRowNumbersByRowId[rowId].push(rowNumber);
            rowNumber++;
        }

        const rightTableData = new TimesheetTableData(data.GeneralUrlParameters, data.SaveUrl, data.RefreshRowDataUrl);
        for (const row of data.RightTableRows)
        {
            rightTableData.addRow(data.RightTableRowsData[row]);
        }

        const commentsManager = new TimesheetCommentsManager(data.RightTableColumns, data.RightTableRows, data.RightTableComments,
            data.CommentSaveUrl, data.RightTableRowsWithEditableComments);
        this.rightTableDataManager = new TimesheetRightTableDataManager(data.RightTableColumns, data.RightTableRows, this.shortcuts,
            rightTableData, (updateData: TimesheetUpdateData) => this.updateData(updateData));
        const formatter = new TimesheetRightTableFormatter(data.RightTableColumns, data.RightTableBorderRows, this.rightTableDataManager);

        this.rightTable = new TimesheetRightTable(data.RightTableColumns, data.RowHeight, data.RowHeaderWidth,
            this.width, data.MultiCellButtons, data.IsCompact, data.HideRightTimeSheetShortcuts, data.DisableDeletingMoreThanOneItem);
        this.rightTable.appendTo(container, this.rightTableDataManager, formatter, commentsManager, this.calculator, this.shortcuts);
        this.rightTable.setHeaderHeight(this.leftTable.getHeaderHeight());
    }

    destroy(): void
    {
        this.leftTable?.destroy();
        this.rightTable?.destroy();
    }

    private updateData(updateData: TimesheetUpdateData)
    {
        this.setCalculationsData(updateData.CalculationsData);
        // Right table header calculations need right table render to be updated which is done when setDataAtCellsWithoutSave is called

        let startColumn = 0;
        for (const column of this.data.RightTableColumns)
        {
            if (column.Date === updateData.DisplayDataStartDate)
            {
                break;
            }
            startColumn++;
        }
        const changes: any[][] = [];
        for (const rowId in updateData.DisplayData)
        {
            const rowData = updateData.DisplayData[rowId];
            for (const rowNumber of this.rightTableRowNumbersByRowId[rowId])
            {
                for (let i = 0; i < rowData.length; i++)
                {
                    this.rightTableDataManager.setCellData(rowNumber, startColumn + i, rowData[i]);
                    changes.push([rowNumber, startColumn + i, rowData[i].EditValue]);
                }
            }
        }
        this.rightTable.setDataAtCellsWithoutSave(changes);
    }

    private setCalculationsData(data: EditableTableCalculationsData[])
    {
        for (const x of data)
        {
            this.calculator.setCalculationValue(x);
        }
        this.calculator.refreshCalculations();
    }
}
